import type { IdeaCardScoreUnit, ScoreRange } from '@/lib/idea-card-types';

/**
 * Formatting constants for score pills
 */
const COMPACT_THRESHOLD = 1000;
const COMPACT_DECIMALS = 1;
const PERCENT_DECIMALS = 2;
const RATING_DECIMALS = 1;
const DEFAULT_DECIMALS = 0;
const DEFAULT_MAX_DECIMALS = 2;

/**
 * Format a number in compact notation (7200 → "7.2K")
 */
const formatCompact = (value: number): string => {
  if (Math.abs(value) >= 1_000_000) {
    return (value / 1_000_000).toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }) + 'M';
  }
  if (Math.abs(value) >= 1_000) {
    return (value / 1_000).toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }) + 'K';
  }
  return value.toString();
};

export const formatScorePill = (
  score: number | ScoreRange | undefined,
  unit?: IdeaCardScoreUnit,
  label?: string
): string => {
  // Handle range scores
  if (score && typeof score === 'object' && 'lower' in score) {
    const lower = formatCompact(score.lower);
    const upper = formatCompact(score.upper);
    const unitText = score.unit || '';
    return `${lower} - ${upper}${unitText ? ' ' + unitText : ''}`.trim();
  }

  // Existing logic for numeric scores
  if (typeof score === 'number') {
    const isCompact = unit === 'count' && Math.abs(score) >= COMPACT_THRESHOLD;
    const decimals = (unit === 'percent' || unit === 'rate') ? PERCENT_DECIMALS : unit === 'rating' ? RATING_DECIMALS : DEFAULT_DECIMALS;

    let formatted = score.toLocaleString('en-US', {
      notation: isCompact ? 'compact' : 'standard',
      minimumFractionDigits: isCompact ? COMPACT_DECIMALS : (unit === undefined ? DEFAULT_DECIMALS : decimals),
      maximumFractionDigits: isCompact ? COMPACT_DECIMALS : (unit === undefined ? DEFAULT_MAX_DECIMALS : decimals),
    });

    // Add label prefix if provided
    if (label) {
      formatted = `${label} ${formatted}`;
    }

    // Add unit suffix
    if (unit === 'percent' || unit === 'rate') {
      formatted += '%';
    }

    return formatted;
  }

  return '';
};

export const shouldDisplayScorePill = (
  score?: number | ScoreRange
): score is number | ScoreRange => {
  if (typeof score === 'number') {
    return Number.isFinite(score);
  }
  if (score && typeof score === 'object' && 'lower' in score) {
    return (
      Number.isFinite(score.lower) &&
      Number.isFinite(score.upper) &&
      score.lower < score.upper
    );
  }
  return false;
};
